<template>
  <v-navigation-drawer
    :value="value"
    @input="$emit('input', $event)"
    right
    fixed
    temporary
    width="420"
    class="whatsapp-drawer"
  >
    <div class="d-flex align-items-start justify-content-between p-6">
      <div>
        <h3 class="font-weight-bolder text-dark mb-1">WhatsApp Conversation</h3>
        <div class="font-weight-bold text-dark-75">{{ talentName }}</div>
        <div v-if="whatsapp && whatsapp.phone" class="text-muted font-size-sm">
          Sent to {{ whatsapp.phone }}
        </div>
      </div>
      <i
        @click.stop="$emit('input', false)"
        class="flaticon-cancel text-dark font-weight-bold cursor-pointer whatsapp-drawer__close"
      ></i>
    </div>

    <div v-if="whatsapp" class="px-6 pb-6">
      <p v-if="!whatsapp.messages.length" class="text-muted">No messages yet</p>
      <div
        v-for="(message, index) in whatsapp.messages"
        :key="index"
        class="whatsapp-drawer__message"
        :class="{ 'whatsapp-drawer__message--outgoing': message.is_outgoing }"
      >
        <div class="whatsapp-drawer__bubble">
          <template v-if="message.is_outgoing">
            <div
              v-if="message.body"
              class="whatsapp-drawer__text"
              v-html="whatsAppHtml(message.body)"
            />
            <div v-else>Opportunity message (template)</div>
            <div
              v-if="message.buttons && message.buttons.length"
              class="whatsapp-drawer__buttons"
            >
              <div
                v-for="title in message.buttons"
                :key="title"
                class="whatsapp-drawer__button"
                :class="{
                  'whatsapp-drawer__button--chosen':
                    chosenButtons[index] === title,
                }"
                v-text="chosenButtons[index] === title ? `✓ ${title}` : title"
              />
            </div>
            <template v-if="index === latestOutgoingIndex">
              <div v-if="isFailure" class="whatsapp-drawer__meta">
                <v-icon x-small :color="delivery.color">{{
                  delivery.icon
                }}</v-icon>
                <span :style="{ color: delivery.color }">{{
                  whatsapp.status.name
                }}</span>
                · {{ formatTime(whatsapp.sent_at || message.at) }}
                <div v-if="whatsapp.failure">
                  {{ whatsapp.failure.name }}
                </div>
              </div>
              <template v-else>
                <div
                  v-for="step in timeline"
                  :key="step.label"
                  class="whatsapp-drawer__meta"
                >
                  <v-icon x-small :color="step.color">{{ step.icon }}</v-icon>
                  {{ step.label }} · {{ formatTime(step.at) }}
                </div>
              </template>
            </template>
            <div v-else class="whatsapp-drawer__meta">
              {{ formatTime(message.at) }}
            </div>
          </template>
          <template v-else>
            <div v-if="repliedTo(message)" class="whatsapp-drawer__quote">
              <div
                v-if="repliedTo(message).body"
                class="whatsapp-drawer__quote-text"
                v-html="whatsAppHtml(repliedTo(message).body)"
              />
              <div v-else>Opportunity message (template)</div>
              <div class="whatsapp-drawer__meta">
                Sent {{ formatTime(repliedTo(message).at) }}
              </div>
            </div>
            <div>
              tapped <strong>[{{ message.body }}]</strong>
            </div>
            <div class="whatsapp-drawer__meta">
              {{ formatTime(message.at) }}
            </div>
          </template>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import dayjs from "dayjs";
import escape from "lodash/escape";
import {
  WHATSAPP_DELIVERY_STATUSES,
  WHATSAPP_FAILURE_STATUSES,
} from "@/constants/whatsapp-delivery";

export default {
  name: "WhatsAppConversationDrawer",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    talentName: {
      type: String,
      default: "",
    },
    // The row's `whatsapp` block: status, timestamps and messages (oldest first).
    whatsapp: {
      type: Object,
      default: null,
    },
  },
  computed: {
    // Our sends by SID, so a tap can point at the one it answered
    sendIndexBySid() {
      const indexes = {};

      this.whatsapp.messages.forEach((message, index) => {
        if (message.is_outgoing && message.sid) {
          indexes[message.sid] = index;
        }
      });

      return indexes;
    },
    // The button the talent tapped on each of our messages (by index): the send the tap answered (an earlier
    // one after a resend), else our latest message before it; a later tap on the same message shows the latest
    chosenButtons() {
      const chosen = {};
      let outgoingIndex = null;

      this.whatsapp.messages.forEach((message, index) => {
        if (message.is_outgoing) {
          outgoingIndex = index;
          return;
        }

        const answered =
          this.sendIndexBySid[message.parent_sid] ?? outgoingIndex;

        if (answered !== null) {
          chosen[answered] = message.body;
        }
      });

      return chosen;
    },
    latestOutgoingIndex() {
      return this.whatsapp.messages
        .map((message) => message.is_outgoing)
        .lastIndexOf(true);
    },
    delivery() {
      return WHATSAPP_DELIVERY_STATUSES[this.whatsapp.status.id] || {};
    },
    isFailure() {
      return WHATSAPP_FAILURE_STATUSES.includes(this.whatsapp.status.id);
    },
    // The steps of the latest send, labelled by the API; only the icon and color are decided here
    timeline() {
      return this.whatsapp.timeline.map((step) => ({
        ...WHATSAPP_DELIVERY_STATUSES[step.status.id],
        label: step.status.name,
        at: step.at,
      }));
    },
  },
  mounted() {
    document.addEventListener("keydown", this.closeOnEscape);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.closeOnEscape);
  },
  methods: {
    // The send a tap answered, when we have it
    repliedTo(message) {
      const index = this.sendIndexBySid[message.parent_sid];

      return index === undefined ? null : this.whatsapp.messages[index];
    },
    closeOnEscape(event) {
      if (event.key === "Escape" && this.value) {
        this.$emit("input", false);
      }
    },
    // WhatsApp's formatting as HTML, applied to the escaped text: ```monospace```, `code`, *bold*, _italic_
    // and ~strikethrough~. A marker only counts at a word's edge, as on WhatsApp, so snake_case stays as it is
    whatsAppHtml(text) {
      return escape(text)
        .replace(/```([\s\S]+?)```/g, "<code>$1</code>")
        .replace(/`([^`\n]+)`/g, "<code>$1</code>")
        .replace(/(^|\W)\*(\S(?:[^*\n]*\S)?)\*(?!\w)/g, "$1<strong>$2</strong>")
        .replace(/(^|\W)_(\S(?:[^_\n]*\S)?)_(?!\w)/g, "$1<em>$2</em>")
        .replace(/(^|\W)~(\S(?:[^~\n]*\S)?)~(?!\w)/g, "$1<s>$2</s>");
    },
    formatTime(date) {
      return date ? dayjs(date).format("DD/MM/YYYY hh:mm a") : "-";
    },
  },
};
</script>

<style scoped>
.whatsapp-drawer {
  z-index: 96 !important;
}

.whatsapp-drawer__close {
  font-size: 2rem;
}

.whatsapp-drawer__message {
  display: flex;
  margin-bottom: 12px;
}

.whatsapp-drawer__message--outgoing {
  justify-content: flex-end;
}

.whatsapp-drawer__bubble {
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: #f1f2f4;
  color: #1f2430;
}

.whatsapp-drawer__message--outgoing .whatsapp-drawer__bubble {
  background-color: #efebfc;
}

/* The send a tap answered, quoted above it as WhatsApp does */
.whatsapp-drawer__quote {
  margin-bottom: 6px;
  padding: 4px 8px;
  border-left: 3px solid #6c5ce7;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.05);
  font-size: 12px;
}

.whatsapp-drawer__quote-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.whatsapp-drawer__text {
  white-space: pre-wrap;
  word-break: break-word;
}

.whatsapp-drawer__meta {
  margin-top: 4px;
  font-size: 11px;
  color: #6b7280;
}

/* The template's quick-reply buttons, shown like WhatsApp's: full-width rows under the message */
/* Under the text, above the status lines */
.whatsapp-drawer__buttons {
  margin: 8px -12px 0;
  border-bottom: 1px solid #ddd6f3;
}

.whatsapp-drawer__button {
  padding: 8px 12px;
  border-top: 1px solid #ddd6f3;
  text-align: center;
  color: #0b84c6;
  font-weight: 500;
}

.whatsapp-drawer__button--chosen {
  background-color: #e2dbfa;
  font-weight: 700;
}
</style>
